import { type FormEvent, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertCircle } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import type { EnrollTokenCreated, EnrollTokenInput, Group } from '@/lib/types'
import type { DeviceMode } from '@/protocol'
import { Button, CopyButton, Dialog, Field, Input, Select } from './ui'
import { AgentDownloadMenu } from './AgentDownloads'
import { rememberPlainToken } from '@/pages/Settings'

/**
 * "Add device" = create an enrollment token and hand the operator the one-line installers.
 * The plain token is only ever shown here.
 */
export function AddDeviceDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const [label, setLabel] = useState('')
  const [expires, setExpires] = useState('24')
  const [maxUses, setMaxUses] = useState('1')
  const [mode, setMode] = useState<DeviceMode>('unattended')
  const [tags, setTags] = useState('')
  const [groupId, setGroupId] = useState('')
  const groups = useQuery({ queryKey: ['groups'], queryFn: () => api.get<Group[]>('/api/groups'), enabled: open })
  const [created, setCreated] = useState<EnrollTokenCreated | null>(null)

  const create = useMutation({
    mutationFn: (input: EnrollTokenInput) => api.post<EnrollTokenCreated>('/api/enroll-tokens', input),
    onSuccess: (t) => {
      setCreated(t)
      rememberPlainToken(t.id, t.token)
      qc.invalidateQueries({ queryKey: ['enroll-tokens'] })
    },
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    create.mutate({
      label: label.trim() || 'Untitled',
      expires_in_hours: expires ? Number(expires) : undefined,
      max_uses: maxUses ? Number(maxUses) : undefined,
      default_mode: mode,
      default_tags: tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      default_group_id: groupId || undefined,
    })
  }

  const close = () => {
    onClose()
    setTimeout(() => {
      setCreated(null)
      create.reset()
    }, 200)
  }

  return (
    <Dialog open={open} onClose={close} title={created ? 'Install the agent' : 'Add a device'} width="max-w-xl">
      {!created ? (
        <form onSubmit={submit} className="flex flex-col gap-3">
          <p className="text-ink-muted -mt-1">
            Create an enrollment token, then run the installer on the machine. It downloads the agent, enrolls it here and starts the
            service.
          </p>
          <Field label="Label">
            <Input autoFocus value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Front desk PCs" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Expires after">
              <Select
                value={expires}
                onChange={setExpires}
                options={[
                  { value: '1', label: '1 hour' },
                  { value: '24', label: '24 hours' },
                  { value: '168', label: '7 days' },
                  { value: '720', label: '30 days' },
                  { value: '', label: 'Never' },
                ]}
              />
            </Field>
            <Field label="Maximum uses">
              <Select
                value={maxUses}
                onChange={setMaxUses}
                options={[
                  { value: '1', label: '1 device' },
                  { value: '10', label: '10 devices' },
                  { value: '100', label: '100 devices' },
                  { value: '', label: 'Unlimited' },
                ]}
              />
            </Field>
            <Field label="Default mode">
              <Select
                value={mode}
                onChange={(v) => setMode(v as DeviceMode)}
                options={[
                  { value: 'unattended', label: 'Unattended', description: 'Connect any time' },
                  { value: 'help_me', label: 'Help me', description: 'User approves each session' },
                ]}
              />
            </Field>
            <Field label="Default tags" hint="Comma separated.">
              <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="office, windows" />
            </Field>
            <Field label="Default group" className="col-span-2">
              <Select
                value={groupId}
                onChange={setGroupId}
                options={[
                  { value: '', label: 'No group (admins only)' },
                  ...(groups.data ?? [])
                    .slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((g) => ({ value: g.id, label: g.name })),
                ]}
              />
            </Field>
          </div>
          {create.isError && (
            <div className="flex items-center gap-2 rounded-md bg-danger-soft px-3 py-2 text-[12.5px] text-danger">
              <AlertCircle size={14} /> {errorMessage(create.error)}
            </div>
          )}
          <div className="mt-2 flex justify-end gap-2">
            <Button type="button" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={create.isPending}>
              Create token
            </Button>
          </div>
        </form>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="rounded-md border border-warn/40 bg-warn-soft px-3 py-2 text-[12.5px]">
            The token is shown only once. Copy an installer command now.
          </div>
          <Installer title="macOS" hint="Terminal (sudo)" command={created.install.macos} />
          <Installer title="Windows" hint="PowerShell (admin)" command={created.install.windows} />
          <div className="flex items-center justify-between gap-3 rounded-md border border-line bg-raised px-3 py-2">
            <div className="text-[12.5px] text-ink-muted">
              <div className="font-medium text-ink">Or download the agent</div>
              A download with this token baked in: it carries the console address and branding, enrolls itself and needs no command line.
            </div>
            <AgentDownloadMenu token={created.token} label="Download agent" align="right" />
          </div>
          <div>
            <div className="eyebrow mb-1">Token</div>
            <div className="flex items-center gap-2">
              <code className="mono flex-1 truncate rounded-md border border-line bg-raised px-2 py-1.5">{created.token}</code>
              <CopyButton text={created.token} />
            </div>
          </div>
          <div className="flex justify-end">
            <Button variant="primary" onClick={close}>
              Done
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  )
}

function Installer({ title, hint, command }: { title: string; hint: string; command: string }) {
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between">
        <div className="font-medium">{title}</div>
        <div className="text-[11.5px] text-ink-faint">{hint}</div>
      </div>
      <div className="flex items-start gap-2">
        <pre className="mono flex-1 overflow-x-auto rounded-md border border-line bg-raised px-2.5 py-2 whitespace-pre-wrap break-all">{command}</pre>
        <CopyButton text={command} />
      </div>
    </div>
  )
}
